import axios from "axios";

const CACHE_PREFIX = "offline_cache_v1";
const QUEUE_KEY = `${CACHE_PREFIX}:queue`;
const AUTH_KEY = `${CACHE_PREFIX}:auth`;
const PROFILE_KEY = `${CACHE_PREFIX}:profile`;
const RESTORE_KEY = `${CACHE_PREFIX}:restore`;
const SYNCED_REPLIES_KEY = `${CACHE_PREFIX}:synced_replies`;

const safeRead = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
};

const safeWrite = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {}
};

const sessionsKey = (userId) => `${CACHE_PREFIX}:sessions:${userId}`;
const analysisKey = (sessionId) => `${CACHE_PREFIX}:analysis:${sessionId}`;
const userStatsKey = (userId) => `${CACHE_PREFIX}:user_stats:${userId}`;

export const getCachedSessions = (userId) => {
  if (!userId) return null;
  return safeRead(sessionsKey(userId), null);
};

export const setCachedSessions = (userId, sessions) => {
  if (!userId || !Array.isArray(sessions)) return;
  safeWrite(sessionsKey(userId), {
    sessions,
    updatedAt: Date.now(),
  });
};

export const getCachedAnalysisDetails = (sessionId) => {
  if (!sessionId) return null;
  return safeRead(analysisKey(sessionId), null);
};

export const setCachedAnalysisDetails = (sessionId, details) => {
  if (!sessionId || !details) return;
  safeWrite(analysisKey(sessionId), {
    details,
    updatedAt: Date.now(),
  });
};

export const getCachedUserStats = (userId) => {
  if (!userId) return null;
  return safeRead(userStatsKey(userId), null);
};

export const setCachedUserStats = (userId, stats) => {
  if (!userId || !stats) return;
  safeWrite(userStatsKey(userId), {
    stats,
    updatedAt: Date.now(),
  });
};

export const setCachedAuthSnapshot = (session) => {
  if (!session?.user?.id) return;
  safeWrite(AUTH_KEY, {
    user: {
      id: session.user.id,
      email: session.user.email || null,
    },
    updatedAt: Date.now(),
  });
};

export const getCachedAuthSnapshot = () => safeRead(AUTH_KEY, null);

export const clearCachedAuthSnapshot = () => {
  try {
    localStorage.removeItem(AUTH_KEY);
  } catch (_) {}
};

export const setCachedProfile = (profile) => {
  if (!profile?.id) return;
  safeWrite(PROFILE_KEY, {
    profile,
    updatedAt: Date.now(),
  });
};

export const getCachedProfile = () => safeRead(PROFILE_KEY, null);

export const setCachedRestorePayload = (sessionId, payload) => {
  if (!sessionId || !payload) return;
  const current = safeRead(RESTORE_KEY, {});
  current[String(sessionId)] = {
    payload,
    updatedAt: Date.now(),
  };
  safeWrite(RESTORE_KEY, current);
};

export const getCachedRestorePayload = (sessionId) => {
  if (!sessionId) return null;
  const current = safeRead(RESTORE_KEY, {});
  return current[String(sessionId)] || null;
};

const readQueue = () => safeRead(QUEUE_KEY, []);
const writeQueue = (queue) => safeWrite(QUEUE_KEY, queue);
const readSyncedReplies = () => safeRead(SYNCED_REPLIES_KEY, []);
const writeSyncedReplies = (items) => safeWrite(SYNCED_REPLIES_KEY, items);

export const enqueueFavoriteToggle = ({ userId, sessionId }) => {
  if (!userId || !sessionId) return;
  const queue = readQueue();
  queue.push({
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    type: "toggle-favorite",
    userId,
    sessionId,
    createdAt: Date.now(),
    retries: 0,
  });
  writeQueue(queue);
};

export const enqueueAiChatMessage = ({
  userId,
  videoId,
  context,
  conversationHistory,
  parentName,
  childName,
}) => {
  if (!userId || !videoId || !Array.isArray(conversationHistory)) return;
  const queue = readQueue();
  queue.push({
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    type: "ai-chat",
    userId,
    videoId,
    context,
    conversationHistory,
    parentName: parentName || "",
    childName: childName || "",
    createdAt: Date.now(),
    retries: 0,
  });
  writeQueue(queue);
};

export const consumeSyncedAiReplies = ({ userId, videoId }) => {
  const all = readSyncedReplies();
  if (!all.length) return [];
  const consumed = [];
  const remaining = [];

  for (const item of all) {
    if (
      String(item.userId || "") === String(userId || "") &&
      String(item.videoId || "") === String(videoId || "")
    ) {
      consumed.push(item);
    } else {
      remaining.push(item);
    }
  }

  writeSyncedReplies(remaining);
  return consumed;
};

export const getPendingQueueSize = () => readQueue().length;

export const flushOfflineQueue = async (apiBaseUrl) => {
  if (!apiBaseUrl) return { processed: 0, remaining: getPendingQueueSize() };

  const queue = readQueue();
  if (!queue.length) return { processed: 0, remaining: 0 };

  let processed = 0;
  const remaining = [];

  for (const item of queue) {
    if (item.type === "toggle-favorite") {
      try {
        await axios.post(`${apiBaseUrl}/api/toggle-favorite`, {
          sessionId: item.sessionId,
          userId: item.userId,
        });
        processed += 1;
        continue;
      } catch (_) {
        remaining.push({
          ...item,
          retries: Number(item.retries || 0) + 1,
        });
        break;
      }
    }

    if (item.type === "ai-chat") {
      try {
        const { data } = await axios.post(`${apiBaseUrl}/api/ai-chat`, {
          videoId: item.videoId,
          conversationHistory: item.conversationHistory,
          context: item.context || "free_qa",
          parentName: item.parentName || "",
          childName: item.childName || "",
          userId: item.userId,
        });
        if (data?.botReply || data?.quickReplies?.length) {
          const replies = readSyncedReplies();
          replies.push({
            id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            userId: item.userId,
            videoId: item.videoId,
            botReply: data.botReply || "",
            quickReplies: Array.isArray(data.quickReplies)
              ? data.quickReplies
              : [],
            transferToSlp: Boolean(data.transferToSlp),
            createdAt: Date.now(),
          });
          writeSyncedReplies(replies);
        }
        processed += 1;
        continue;
      } catch (_) {
        remaining.push({
          ...item,
          retries: Number(item.retries || 0) + 1,
        });
        break;
      }
    }

    if (item.type !== "toggle-favorite" && item.type !== "ai-chat") {
      remaining.push(item);
    }
  }

  writeQueue(remaining);
  return { processed, remaining: remaining.length };
};
